import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "pt-BR", label: "Português (Brasil)" },
  { value: "es-419", label: "Español (Latinoamérica)" },
  { value: "fr-CA", label: "Français (Canada)" },
];

export function AdminUserLanguageControl({ userId }) {
  const [language, setLanguage] = useState("en");
  const [savedLanguage, setSavedLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/admin/users/${userId}/language`)
      .then(({ data }) => {
        if (!active) return;
        const value = data.preferred_language || "en";
        setLanguage(value);
        setSavedLanguage(value);
      })
      .catch((ex) => {
        if (active) toast.error(formatApiErrorDetail(ex));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [userId]);

  const save = async () => {
    if (language === savedLanguage) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}/language`, {
        preferred_language: language,
        reason: "Admin language change for support/testing",
      });
      setSavedLanguage(language);
      toast.success("Application language updated");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
      setLanguage(savedLanguage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)]/40 p-4" data-testid="admin-user-language-control">
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">Application language</div>
      <p className="mt-1 text-xs text-[color:var(--text-secondary)]">
        Change this for support or testing. The user will see the selected language on their next app view/refresh.
      </p>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={loading || saving}
          className="min-w-[220px] rounded-lg border border-[color:var(--border-stone)] bg-white px-3 py-2 text-sm"
          data-testid="admin-user-language-select"
        >
          {LANGUAGES.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={save}
          disabled={loading || saving || language === savedLanguage}
          className="rounded-full bg-[color:var(--brand)] px-4 py-2 text-xs font-medium text-white hover:bg-[color:var(--brand-hover)] disabled:opacity-40"
          data-testid="admin-user-language-save"
        >
          {saving ? "Saving…" : "Save language"}
        </button>
      </div>
    </div>
  );
}
